/**
 * Cost tab (admin only — the whole /admin panel is admin-gated and every
 * /api/admin/cost/* route re-enforces it server-side).
 *
 * The cost-visibility centerpiece (docs/ADMIN_OBSERVABILITY_AND_COST_PLAN.md):
 *   - Headline: today's spend + a 7-day trend (inline CSS bars — no chart lib).
 *   - Breakdown by category (stt_live / stt_batch / live_coaching /
 *     call_scoring / call_classifier / …) with tokens + STT seconds alongside
 *     dollars, plus a stacked share bar.
 *   - Date-range filter (presets + custom from/to).
 *   - Top-N most-expensive calls, each linking to CallDetail.
 *   - An "estimates" footnote: STT batch/Deepgram rates in lib/pricing.ts are
 *     best-knowledge list prices pending invoice verification.
 *
 * All data comes from the admin-only cost API (server/src/routes/cost.ts) over
 * the v_cost_events view — one row per dollar-spending event, money in cents.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, SelectField, Badge } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { centsToDollars, formatTokens, formatDateTime, secToClock } from "@/lib/format";

/** Friendly labels for the v_cost_events categories (fallback = raw key). */
const CATEGORY_LABELS: Record<string, string> = {
  stt_live: "STT — live streaming",
  stt_batch: "STT — batch",
  live_coaching: "Live coaching (nudges)",
  call_scoring: "After-call scoring",
  call_classifier: "Classifier",
  call_classify: "Classifier",
  value_builders: "Value-builders",
};

function categoryLabel(key: string): string {
  return CATEGORY_LABELS[key] ?? key;
}

/** Stable color per stacked-bar slice (cycles when there are more categories). */
const PALETTE = [
  "bg-primary",
  "bg-band-solid",
  "bg-band-fair",
  "bg-band-weak",
  "bg-sky-500",
  "bg-violet-500",
];

/** 'YYYY-MM-DD' local-day key in a zone (mirrors the server's dayKeyInTz). */
function dayKeyInTz(d: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    // Bad zone string — fall back to the browser zone rather than crash.
    return new Intl.DateTimeFormat("en-CA").format(d);
  }
}

/** Minutes the zone's wall clock is ahead of UTC at a given instant. */
function tzOffsetMinutes(at: Date, timeZone: string): number {
  // en-CA + h23 → "YYYY-MM-DD, HH:mm:ss"; re-parse the wall-clock reading as
  // if it were UTC and diff against the real instant.
  const wall = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).format(at);
  const [datePart, timePart] = wall.split(", ");
  return (new Date(`${datePart}T${timePart}Z`).getTime() - at.getTime()) / 60_000;
}

/**
 * The UTC instant of local midnight (start of "today") in an IANA zone, so the
 * "Today" preset agrees with the tenant-day "Spend today" headline card. Falls
 * back to browser-local midnight on a bad zone string.
 */
function startOfTodayInTz(timeZone: string | undefined): Date {
  const now = new Date();
  try {
    if (!timeZone) throw new Error("no timezone");
    const key = dayKeyInTz(now, timeZone); // today's YYYY-MM-DD in the zone
    const utcGuess = new Date(`${key}T00:00:00Z`);
    // Shift by the zone offset at that instant. (US zones flip DST at 2am
    // local, never at midnight, so one correction pass is exact here.)
    return new Date(utcGuess.getTime() - tzOffsetMinutes(utcGuess, timeZone) * 60_000);
  } catch {
    const midnight = new Date(now);
    midnight.setHours(0, 0, 0, 0);
    return midnight;
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

type RangePreset = "today" | "7d" | "30d" | "custom";

const PRESET_OPTIONS: Array<{ value: RangePreset; label: string }> = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "custom", label: "Custom range" },
];

function presetRange(
  preset: RangePreset,
  timeZone?: string,
): { from?: string; to?: string } {
  const now = Date.now();
  switch (preset) {
    case "today":
      // Tenant-zone midnight (the API reports it on range.timezone), so this
      // filter matches the headline "Spend today" card exactly.
      return { from: startOfTodayInTz(timeZone).toISOString() };
    case "7d":
      return { from: new Date(now - 7 * DAY_MS).toISOString() };
    case "30d":
    default:
      return { from: new Date(now - 30 * DAY_MS).toISOString() };
  }
}

export function CostTab() {
  // ── Range state ────────────────────────────────────────────────────────────
  const [preset, setPreset] = useState<RangePreset>("30d");
  // Custom date drafts (yyyy-mm-dd from <input type="date">) + the APPLIED range.
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [range, setRange] = useState<{ from?: string; to?: string }>(() =>
    presetRange("30d"),
  );

  function selectPreset(p: RangePreset) {
    setPreset(p);
    // The tenant timezone rides on the (already-loaded) trend response — the
    // range UI only renders after that query resolves, so it's available here.
    if (p !== "custom") setRange(presetRange(p, trendQuery.data?.range.timezone));
  }

  function applyCustom() {
    if (!customFrom) return;
    const from = new Date(`${customFrom}T00:00:00`);
    // End of the chosen to-day (inclusive); empty "to" = now.
    const to = customTo ? new Date(`${customTo}T23:59:59.999`) : null;
    if (Number.isNaN(from.getTime()) || (to && Number.isNaN(to.getTime()))) return;
    setRange({ from: from.toISOString(), ...(to ? { to: to.toISOString() } : {}) });
  }
  const customInvalid =
    preset === "custom" && customFrom !== "" && customTo !== "" && customFrom > customTo;

  // ── Queries ────────────────────────────────────────────────────────────────
  // Headline trend: a FIXED last-7-days window, independent of the filter below,
  // so "today + the week" stays visible while exploring other ranges.
  const [trendRange] = useState(() => ({
    from: new Date(Date.now() - 7 * DAY_MS).toISOString(),
  }));
  const trendQuery = useQuery({
    queryKey: ["admin", "cost", "summary", trendRange],
    queryFn: ({ signal }) => api.adminCostSummary(trendRange, signal),
  });

  const summaryQuery = useQuery({
    queryKey: ["admin", "cost", "summary", range],
    queryFn: ({ signal }) => api.adminCostSummary(range, signal),
  });

  const topCallsQuery = useQuery({
    queryKey: ["admin", "cost", "top-calls", range],
    queryFn: ({ signal }) => api.adminCostTopCalls({ ...range, limit: 20 }, signal),
  });

  if (trendQuery.isLoading || summaryQuery.isLoading) {
    return <LoadingState label="Loading cost data…" />;
  }
  if (trendQuery.isError || !trendQuery.data) {
    return <ErrorState error={trendQuery.error} onRetry={() => void trendQuery.refetch()} />;
  }
  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <ErrorState error={summaryQuery.error} onRetry={() => void summaryQuery.refetch()} />
    );
  }

  const trend = trendQuery.data;
  const summary = summaryQuery.data;

  // ── Headline numbers (today + last-7-days, tenant-local days) ─────────────
  const tz = trend.range.timezone;
  const todayKey = dayKeyInTz(new Date(), tz);
  const centsByDay = new Map(trend.by_day.map((d) => [d.day, d.cost_cents]));
  const todayCents = centsByDay.get(todayKey) ?? 0;
  const last7Keys: string[] = [];
  for (let i = 6; i >= 0; i--) {
    last7Keys.push(dayKeyInTz(new Date(Date.now() - i * DAY_MS), tz));
  }
  const trendBars = last7Keys.map((day) => ({ day, cents: centsByDay.get(day) ?? 0 }));
  const trendMax = Math.max(...trendBars.map((b) => b.cents), 1);
  const week7Cents = trendBars.reduce((s, b) => s + b.cents, 0);

  const totalCents = summary.total_cents;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-sm font-semibold">Cost</h2>
        <p className="text-sm text-muted-foreground">
          Every dollar the system spends (Claude + speech-to-text), per shop-local day
          ({tz}).
        </p>
      </div>

      {/* ── Headline: today + 7-day trend ─────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="flex flex-col gap-1">
          <p className="text-xs font-medium text-muted-foreground">Spend today</p>
          <p className="text-2xl font-semibold">{centsToDollars(todayCents)}</p>
          <p className="text-xs text-muted-foreground">
            Local day {todayKey} — resets at midnight {tz}.
          </p>
        </Card>
        <Card className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-xs font-medium text-muted-foreground">Last 7 days</p>
            <p className="text-sm font-semibold">{centsToDollars(week7Cents)}</p>
          </div>
          <div className="flex h-12 items-end gap-1" aria-hidden>
            {trendBars.map((b) => (
              <div
                key={b.day}
                className={
                  "flex-1 rounded-t-sm " + (b.cents > 0 ? "bg-primary/80" : "bg-muted")
                }
                style={{
                  height: `${b.cents > 0 ? Math.max((b.cents / trendMax) * 100, 8) : 4}%`,
                }}
                title={`${b.day}: ${centsToDollars(b.cents)}`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{trendBars[0]?.day}</span>
            <span>{trendBars[trendBars.length - 1]?.day}</span>
          </div>
        </Card>
      </div>

      {/* ── Range filter ───────────────────────────────────────────────────── */}
      <Card className="flex flex-wrap items-end gap-3">
        <SelectField
          label="Date range"
          options={PRESET_OPTIONS}
          value={preset}
          onChange={(e) => selectPreset(e.target.value as RangePreset)}
          className="min-w-[160px]"
        />
        {preset === "custom" && (
          <>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">From</span>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">To (optional)</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <button
              type="button"
              onClick={applyCustom}
              disabled={!customFrom || customInvalid}
              className="rounded-md border px-3 py-1.5 text-sm font-medium transition hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              Apply
            </button>
            {customInvalid && (
              <span className="pb-1.5 text-sm text-amber-600 dark:text-amber-400">
                "From" must be on or before "To".
              </span>
            )}
          </>
        )}
        <div className="ml-auto pb-1.5 text-sm text-muted-foreground">
          Total: <span className="font-semibold text-foreground">{centsToDollars(totalCents)}</span>{" "}
          · {summary.event_count} events · {formatTokens(summary.total_tokens_in)} in /{" "}
          {formatTokens(summary.total_tokens_out)} out tokens ·{" "}
          {Math.round(summary.total_stt_seconds / 60)} STT min
        </div>
      </Card>

      {summary.truncated && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          This range has more cost events than the dashboard scans at once — totals may
          undercount. Narrow the date range for exact numbers.
        </p>
      )}

      {/* ── Breakdown by category ──────────────────────────────────────────── */}
      <Card className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Where the money goes</h3>
        {summary.by_category.length === 0 ? (
          <p className="text-sm text-muted-foreground">No spend in this range.</p>
        ) : (
          <>
            {/* Stacked share bar */}
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
              {summary.by_category.map((c, i) =>
                c.cost_cents > 0 && totalCents > 0 ? (
                  <div
                    key={c.category}
                    className={PALETTE[i % PALETTE.length]}
                    style={{ width: `${(c.cost_cents / totalCents) * 100}%` }}
                    title={`${categoryLabel(c.category)}: ${centsToDollars(c.cost_cents)}`}
                  />
                ) : null,
              )}
            </div>

            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">Category</th>
                  <th className="py-2 pr-3 font-medium">Cost</th>
                  <th className="py-2 pr-3 font-medium">Share</th>
                  <th className="py-2 pr-3 font-medium">Events</th>
                  <th className="py-2 pr-3 font-medium">Tokens (in / out)</th>
                  <th className="py-2 font-medium">STT time</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_category.map((c, i) => {
                  const sharePct =
                    totalCents > 0 ? Math.round((c.cost_cents / totalCents) * 100) : 0;
                  return (
                    <tr key={c.category} className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        <span className="flex items-center gap-2">
                          <span
                            className={
                              "inline-block h-2.5 w-2.5 rounded-sm " +
                              PALETTE[i % PALETTE.length]
                            }
                            aria-hidden
                          />
                          {categoryLabel(c.category)}
                        </span>
                      </td>
                      <td className="py-2 pr-3 font-medium">{centsToDollars(c.cost_cents)}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{sharePct}%</td>
                      <td className="py-2 pr-3 text-muted-foreground">{c.events}</td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {c.tokens_in > 0 || c.tokens_out > 0
                          ? `${formatTokens(c.tokens_in)} / ${formatTokens(c.tokens_out)}`
                          : "—"}
                      </td>
                      <td className="py-2 text-muted-foreground">
                        {c.stt_seconds > 0 ? `${Math.round(c.stt_seconds / 60)} min` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </Card>

      {/* ── By model ───────────────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">By provider / model</h3>
        {summary.by_model.length === 0 ? (
          <p className="text-sm text-muted-foreground">No spend in this range.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2 pr-3 font-medium">Provider</th>
                <th className="py-2 pr-3 font-medium">Model</th>
                <th className="py-2 pr-3 font-medium">Cost</th>
                <th className="py-2 font-medium">Events</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_model.map((m) => (
                <tr key={`${m.provider}/${m.model}`} className="border-b last:border-0">
                  <td className="py-2 pr-3">{m.provider}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{m.model}</td>
                  <td className="py-2 pr-3 font-medium">{centsToDollars(m.cost_cents)}</td>
                  <td className="py-2 text-muted-foreground">{m.events}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ── Most expensive calls ───────────────────────────────────────────── */}
      <Card className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Most expensive calls</h3>
        {topCallsQuery.isLoading && <LoadingState label="Loading calls…" />}
        {topCallsQuery.isError && (
          <ErrorState
            error={topCallsQuery.error}
            onRetry={() => void topCallsQuery.refetch()}
          />
        )}
        {topCallsQuery.data && topCallsQuery.data.calls.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No per-call spend in this range.
          </p>
        )}
        {topCallsQuery.data && topCallsQuery.data.calls.length > 0 && (
          <>
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Ext</th>
                  <th className="py-2 pr-3 font-medium">Direction</th>
                  <th className="py-2 pr-3 font-medium">Length</th>
                  <th className="py-2 pr-3 font-medium">Cost</th>
                  <th className="py-2 font-medium">Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {topCallsQuery.data.calls.map((c) => (
                  <tr key={c.call_id} className="border-b last:border-0">
                    <td className="py-2 pr-3">
                      <Link
                        to={`/calls/${c.call_id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {c.started_at ? formatDateTime(c.started_at) : "View call"}
                      </Link>
                    </td>
                    <td className="py-2 pr-3">{c.extension_number ?? "—"}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{c.direction ?? "—"}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {c.duration_sec != null ? secToClock(c.duration_sec) : "—"}
                    </td>
                    <td className="py-2 pr-3 font-medium">{centsToDollars(c.cost_cents)}</td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {Object.entries(c.breakdown)
                        .sort((a, b) => b[1] - a[1])
                        .map(([k, cents]) => `${categoryLabel(k)} ${centsToDollars(cents)}`)
                        .join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {topCallsQuery.data.unattributed_cents > 0 && (
              <p className="text-xs text-muted-foreground">
                Plus {centsToDollars(topCallsQuery.data.unattributed_cents)} in this range
                that couldn&apos;t be tied to a specific call (e.g. a live capture whose
                call-log row never arrived) — included in the totals above.
              </p>
            )}
          </>
        )}
      </Card>

      {/* ── Estimates footnote ─────────────────────────────────────────────── */}
      <p className="text-xs text-muted-foreground">
        <Badge tone="warning">Estimates</Badge>{" "}
        Claude token rates are verified (June 2026). Speech-to-text rates are partly
        estimates: AssemblyAI live streaming ($0.15/hr) is confirmed, but the AssemblyAI
        batch rate and the Deepgram rates are best-knowledge list prices pending
        verification against a real vendor invoice. Rates can be corrected without a
        deploy via the per-tenant pricing override.
      </p>
    </div>
  );
}
